import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Trash2, Link2, Search, Calendar, MapPin, Globe, Building2, Ticket, X, CreditCard, Pencil, ExternalLink, QrCode, Download, Users, Plus, Star } from 'lucide-react';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RowActionsMenu } from './RowActionsMenu';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

export function AdminEventsTab({ 
  events: initialEvents, 
  handleDeleteEvent 
}) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('upcoming'); // Default to upcoming events
  const [assigningEvent, setAssigningEvent] = useState(null);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [viewingBankDetails, setViewingBankDetails] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  
  // Slug editing state
  const [editingSlugEvent, setEditingSlugEvent] = useState(null);
  const [slugForm, setSlugForm] = useState({ slug: '', host_slug: '' });
  const [slugSaving, setSlugSaving] = useState(false);
  
  // Ticket URL editing state
  const [editingTicketEvent, setEditingTicketEvent] = useState(null);
  const [ticketUrlForm, setTicketUrlForm] = useState('');
  const [ticketUrlSaving, setTicketUrlSaving] = useState(false);

  useEffect(() => {
    fetchAllEvents();
  }, []);

  const fetchAllEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        setClubs(data.clubs || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToClub = async () => {
    if (!assigningEvent) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/events/${assigningEvent.event_id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ club_id: selectedClubId || null })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        
        // Update local state
        setEvents(events.map(evt => 
          evt.event_id === assigningEvent.event_id 
            ? { ...evt, linked_club_id: selectedClubId, linked_club_name: data.linked_club_name }
            : evt
        ));
        setAssigningEvent(null);
        setSelectedClubId('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to assign event');
      }
    } catch (error) {
      console.error('Error assigning event:', error);
      toast.error('Network error');
    }
  };

  const handleViewBankDetails = async (clubId, clubName) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/clubs/${clubId}/bank-details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBankDetails(data);
        setViewingBankDetails({ clubId, clubName });
      } else {
        toast.error('Failed to load bank details');
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
      toast.error('Network error');
    }
  };

  // Format slug input in real-time
  const formatSlugInput = (value) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 60);
  };

  const openSlugEditor = (event) => {
    setSlugForm({
      slug: event.slug || '',
      host_slug: event.host_slug || ''
    });
    setEditingSlugEvent(event);
  };

  const handleSlugSave = async () => {
    if (!editingSlugEvent) return;
    
    const token = localStorage.getItem('token');
    setSlugSaving(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/events/${editingSlugEvent.event_id}/slugs`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slug: slugForm.slug,
          host_slug: slugForm.host_slug
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Slugs updated! New URL: ${data.url}`);
        
        // Update local state
        setEvents(events.map(evt => 
          evt.event_id === editingSlugEvent.event_id 
            ? { ...evt, slug: data.slug, host_slug: data.host_slug, full_slug: data.full_slug }
            : evt
        ));
        setEditingSlugEvent(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update slugs');
      }
    } catch (error) {
      console.error('Error updating slugs:', error);
      toast.error('Network error');
    } finally {
      setSlugSaving(false);
    }
  };

  const toggleFeatured = async (event) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/events/${event.event_id}/toggle-featured`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Update local state
        setEvents(events.map(evt => 
          evt.event_id === event.event_id 
            ? { ...evt, is_featured: data.is_featured }
            : evt
        ));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to toggle featured');
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Network error');
    }
  };

  // Ticket URL editing functions
  const openTicketUrlEditor = (event) => {
    setTicketUrlForm(event.external_ticket_url || '');
    setEditingTicketEvent(event);
  };

  const saveTicketUrl = async () => {
    if (!editingTicketEvent) return;
    
    setTicketUrlSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/events/${editingTicketEvent.event_id}/ticket-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ external_ticket_url: ticketUrlForm.trim() || null })
      });

      if (res.ok) {
        toast.success('Ticket URL updated');
        // Update local state
        setEvents(events.map(evt => 
          evt.event_id === editingTicketEvent.event_id 
            ? { ...evt, external_ticket_url: ticketUrlForm.trim() || null }
            : evt
        ));
        setEditingTicketEvent(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update ticket URL');
      }
    } catch (error) {
      console.error('Error updating ticket URL:', error);
      toast.error('Network error');
    } finally {
      setTicketUrlSaving(false);
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.linked_club_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.organizer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Check if event is in the past
    const eventDate = event.event_date ? new Date(event.event_date) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPast = eventDate && eventDate < today;
    
    if (filterType === 'all') return matchesSearch;
    if (filterType === 'upcoming') return matchesSearch && !isPast;
    if (filterType === 'past') return matchesSearch && isPast;
    if (filterType === 'club') return matchesSearch && event.source_type === 'club';
    if (filterType === 'organization') return matchesSearch && event.is_organization_event;
    if (filterType === 'scraped') return matchesSearch && event.source_type === 'scraped';
    if (filterType === 'assigned') return matchesSearch && event.linked_club_id;
    if (filterType === 'unassigned') return matchesSearch && !event.linked_club_id && event.source_type === 'scraped';
    if (filterType === 'featured') return matchesSearch && event.is_featured;
    return matchesSearch;
  });

  // Export attendees as CSV
  const exportAttendees = async (event) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/tickets/attendees/${event.event_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (!data.attendees || data.attendees.length === 0) {
          toast.info('No attendees to export yet');
          return;
        }
        
        // Create CSV
        const headers = ['Name', 'Email', 'Ticket Type', 'Quantity', 'Status', 'Checked In', 'Purchase Date', 'Booking Ref'];
        const rows = data.attendees.map(a => [
          a.name || '',
          a.email || '',
          a.ticket_type || '',
          a.quantity || 1,
          a.status || '',
          a.checked_in ? 'Yes' : 'No',
          a.purchase_date ? new Date(a.purchase_date).toLocaleDateString() : '',
          a.booking_ref || ''
        ]);
        
        const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendees-${event.slug || event.event_id}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.attendees.length} attendees`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to export attendees');
      }
    } catch (error) {
      console.error('Error exporting attendees:', error);
      toast.error('Network error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-slate-900">Events Management</h2>
        <Button 
          onClick={() => navigate('/dashboard/add-event')}
          className="bg-emerald-600 hover:bg-emerald-500"
          data-testid="create-organization-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px] bg-white">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="upcoming">📅 Upcoming Events</SelectItem>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="past">🕐 Past Events</SelectItem>
            <SelectItem value="featured">⭐ Featured</SelectItem>
            <SelectItem value="club">Club Events</SelectItem>
            <SelectItem value="organization">Organization Events</SelectItem>
            <SelectItem value="scraped">Scraped Events</SelectItem>
            <SelectItem value="assigned">Assigned to Club</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="bg-white">
          {filteredEvents.length} events
        </Badge>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Event</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Type</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Linked Club</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.slice(0, 100).map(event => (
                <tr key={event.event_id} className={`border-b border-slate-100 hover:bg-slate-50 ${event.is_featured ? 'bg-amber-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{event.title}</p>
                          {event.is_featured && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Featured
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location.substring(0, 40)}{event.location.length > 40 ? '...' : ''}
                            </span>
                          )}
                        </div>
                        {event.ticketing_enabled && (
                          <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-xs">
                            <Ticket className="w-3 h-3 mr-1" />
                            Ticketing
                          </Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={event.source_type === 'club' ? 'default' : 'secondary'}>
                      {event.source_type === 'club' ? (
                        <><Building2 className="w-3 h-3 mr-1" />Club</>
                      ) : (
                        <><Globe className="w-3 h-3 mr-1" />Scraped</>
                      )}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-slate-700">
                      <Calendar className="w-3 h-3" />
                      {event.event_date ? new Date(event.event_date).toLocaleDateString() : 'No date'}
                    </div>
                    {event.event_time && (
                      <p className="text-xs text-slate-500 mt-1">{event.event_time}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {event.linked_club_id || event.club_id ? (
                      <div className="space-y-1">
                        <Badge className="bg-violet-100 text-violet-700">
                          <Link2 className="w-3 h-3 mr-1" />
                          {event.linked_club_name || event.club_name || 'Linked'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-slate-500 p-0 h-auto"
                          onClick={() => handleViewBankDetails(event.linked_club_id || event.club_id, event.linked_club_name || event.club_name)}
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          View Bank Details
                        </Button>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">Not assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Check-In Button - prominent for ticketed events */}
                      {event.ticketing_enabled && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => window.open(`/events/${event.event_id}/checkin`, '_blank')}
                          title="Open ticket check-in scanner"
                          data-testid={`checkin-${event.event_id}`}
                        >
                          <QrCode className="w-4 h-4 mr-1" />
                          Check-In
                        </Button>
                      )}

                      {/* Manage Tickets Button - prominent for all events */}
                      <Button
                        variant={event.ticketing_enabled ? "outline" : "default"}
                        size="sm"
                        className={!event.ticketing_enabled ? "bg-violet-600 hover:bg-violet-700" : ""}
                        onClick={() => navigate(`/dashboard/event/${event.event_id}/tickets`)}
                        title="Manage ticket types and sales"
                        data-testid={`manage-tickets-${event.event_id}`}
                      >
                        <Ticket className="w-4 h-4 mr-1" />
                        {event.ticketing_enabled ? 'Tickets' : 'Add Tickets'}
                      </Button>

                      {(() => {
                        const editItems = [];
                        if (event.is_organization_event) editItems.push({
                          label: 'Edit Event Details',
                          icon: <Pencil className="w-4 h-4 text-slate-600" />,
                          onClick: () => { window.location.href = `/dashboard/edit-event/${event.event_id}`; },
                          testId: `edit-event-${event.event_id}`,
                        });
                        editItems.push({
                          label: 'Edit URL Slug',
                          icon: <Pencil className="w-4 h-4 text-slate-600" />,
                          onClick: () => openSlugEditor(event),
                          testId: `edit-slug-${event.event_id}`,
                        });
                        editItems.push({
                          label: event.linked_club_id ? 'Reassign Club' : 'Assign Club',
                          icon: <Link2 className="w-4 h-4 text-slate-600" />,
                          onClick: () => { setAssigningEvent(event); setSelectedClubId(event.linked_club_id || ''); },
                          testId: `assign-event-${event.event_id}`,
                        });
                        editItems.push({
                          label: event.is_featured ? 'Remove from Featured' : 'Feature Event',
                          icon: <Star className={`w-4 h-4 text-amber-500 ${event.is_featured ? 'fill-current' : ''}`} />,
                          onClick: () => toggleFeatured(event),
                          testId: `feature-event-${event.event_id}`,
                        });
                        editItems.push({
                          label: event.external_ticket_url ? 'Edit External Ticket URL' : 'Add External Ticket URL',
                          icon: <Ticket className="w-4 h-4 text-orange-500" />,
                          onClick: () => openTicketUrlEditor(event),
                          testId: `ticket-url-${event.event_id}`,
                        });

                        const viewItems = [{
                          label: 'View Event Page',
                          icon: <ExternalLink className="w-4 h-4 text-slate-600" />,
                          onClick: () => window.open(`/events/${event.slug || event.event_id}`, '_blank'),
                          testId: `view-event-${event.event_id}`,
                        }];
                        if (event.ticketing_enabled) viewItems.push({
                          label: 'Export Attendees (CSV)',
                          icon: <Download className="w-4 h-4 text-slate-600" />,
                          onClick: () => exportAttendees(event),
                          testId: `export-${event.event_id}`,
                        });

                        const groups = [
                          { label: 'Edit', items: editItems },
                          { label: 'View', items: viewItems },
                        ];
                        if (handleDeleteEvent) groups.push({
                          items: [{
                            label: 'Delete Event',
                            icon: <Trash2 className="w-4 h-4" />,
                            destructive: true,
                            onClick: () => handleDeleteEvent(event.event_id),
                            testId: `delete-event-${event.event_id}`,
                            confirm: {
                              title: 'Delete Event',
                              description: `Are you sure you want to delete "${event.title}"?`,
                              actionLabel: 'Delete',
                            },
                          }],
                        });
                        return <RowActionsMenu groups={groups} triggerTestId={`manage-event-${event.event_id}`} />;
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredEvents.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No events found
          </div>
        )}
        {filteredEvents.length > 100 && (
          <div className="text-center py-4 text-sm text-slate-500 border-t">
            Showing first 100 events. Use search to find specific events.
          </div>
        )}
      </div>

      {/* Assign Event Dialog */}
      <Dialog open={!!assigningEvent} onOpenChange={() => setAssigningEvent(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Event to Club</DialogTitle>
            <DialogDescription>
              {assigningEvent?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Select Club</label>
              <Select value={selectedClubId || "unassign"} onValueChange={(val) => setSelectedClubId(val === "unassign" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a club..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassign">-- Unassign --</SelectItem>
                  {clubs.map(club => (
                    <SelectItem key={club.club_id} value={club.club_id}>
                      {club.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-slate-500">
              Once assigned, the club owner will be able to manage this event and add tickets.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAssigningEvent(null)}>
              Cancel
            </Button>
            <Button onClick={handleAssignToClub}>
              {selectedClubId ? 'Assign to Club' : 'Remove Assignment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={!!viewingBankDetails} onOpenChange={() => setViewingBankDetails(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bank Details</DialogTitle>
            <DialogDescription>
              {viewingBankDetails?.clubName}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {bankDetails?.has_bank_details ? (
              <div className="space-y-3 bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-slate-500">Bank Name:</span>
                  <span className="font-medium">{bankDetails.bank_details.bank_name}</span>
                  
                  <span className="text-slate-500">Account Name:</span>
                  <span className="font-medium">{bankDetails.bank_details.account_name}</span>
                  
                  <span className="text-slate-500">Sort Code:</span>
                  <span className="font-mono font-medium">{bankDetails.bank_details.sort_code}</span>
                  
                  <span className="text-slate-500">Account Number:</span>
                  <span className="font-mono font-medium">{bankDetails.bank_details.account_number}</span>
                </div>
                {bankDetails.bank_details.updated_at && (
                  <p className="text-xs text-slate-400 mt-2">
                    Last updated: {new Date(bankDetails.bank_details.updated_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <CreditCard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No bank details on file</p>
                <p className="text-sm mt-1">The club owner needs to add their bank details</p>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewingBankDetails(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Slugs Dialog */}
      <Dialog open={!!editingSlugEvent} onOpenChange={() => setEditingSlugEvent(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event URL</DialogTitle>
            <DialogDescription>
              {editingSlugEvent?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="host_slug">Host/Club Slug</Label>
              <Input
                id="host_slug"
                placeholder="e.g., dancing-queens-club"
                value={slugForm.host_slug}
                onChange={(e) => setSlugForm({ ...slugForm, host_slug: formatSlugInput(e.target.value) })}
                className="font-mono text-sm"
                data-testid="host-slug-input"
              />
              <p className="text-xs text-slate-500">
                The club or host name portion of the URL (optional). Max 60 characters.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="event_slug">Event Slug</Label>
              <Input
                id="event_slug"
                placeholder="e.g., saturday-night-social"
                value={slugForm.slug}
                onChange={(e) => setSlugForm({ ...slugForm, slug: formatSlugInput(e.target.value) })}
                className="font-mono text-sm"
                data-testid="event-slug-input"
              />
              <p className="text-xs text-slate-500">
                The event name portion of the URL. Max 60 characters.
              </p>
            </div>

            {/* URL Preview */}
            <div className="bg-slate-100 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-slate-600">URL Preview:</p>
              <p className="font-mono text-sm text-violet-600 break-all">
                /events/{slugForm.host_slug ? `${slugForm.host_slug}/` : ''}{slugForm.slug || 'event-name'}
              </p>
            </div>

            <div className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="font-medium text-amber-800 mb-1">Tips:</p>
              <ul className="list-disc list-inside text-xs text-amber-700 space-y-1">
                <li>Use lowercase letters, numbers, and hyphens only</li>
                <li>Spaces are automatically converted to hyphens</li>
                <li>Keep it short and descriptive for better SEO</li>
                <li>Duplicate URLs are not allowed</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditingSlugEvent(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSlugSave} 
              disabled={slugSaving || !slugForm.slug}
              data-testid="save-slug-btn"
            >
              {slugSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Ticket URL Dialog */}
      <Dialog open={!!editingTicketEvent} onOpenChange={(open) => !open && setEditingTicketEvent(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-orange-500" />
              External Ticket URL
            </DialogTitle>
            <DialogDescription>
              {editingTicketEvent?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ticket_url">Ticket Provider URL</Label>
              <Input
                id="ticket_url"
                type="url"
                placeholder="https://www.eventbrite.co.uk/e/..."
                value={ticketUrlForm}
                onChange={(e) => setTicketUrlForm(e.target.value)}
                className="font-mono text-sm"
                data-testid="ticket-url-input"
              />
              <p className="text-xs text-slate-500">
                Enter the full URL to the external ticket provider (e.g., Eventbrite, Ticketmaster, etc.)
              </p>
            </div>

            {ticketUrlForm && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-xs font-medium text-orange-800 mb-1">Preview:</p>
                <p className="text-sm text-orange-700">
                  An orange "Get Tickets" button will appear on this event, linking to:
                </p>
                <a 
                  href={ticketUrlForm} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-orange-600 hover:underline break-all font-mono"
                >
                  {ticketUrlForm}
                </a>
              </div>
            )}

            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="font-medium text-slate-700 mb-1">Note:</p>
              <p className="text-xs text-slate-600">
                Leave empty to remove the external ticket link. The "Get Tickets" button will only appear if a URL is set.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditingTicketEvent(null)}>
              Cancel
            </Button>
            <Button 
              onClick={saveTicketUrl} 
              disabled={ticketUrlSaving}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-ticket-url-btn"
            >
              {ticketUrlSaving ? 'Saving...' : (ticketUrlForm ? 'Save URL' : 'Remove URL')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
